import styled from "styled-components";

/**
 * DragDrop Styled Components Library
 *
 * A collection of styled-components used by the DragDrop file upload component.
 * Provides all necessary styling for file input, preview, and upload UI.
 */

/**
 * FileUploadContainer - Outer container for drag-and-drop file upload area
 *
 * @returns {styled.section} Styled section element with drag-drop styling
 */
export const FileUploadContainer = styled.section`
                                   position: relative;
margin: 25px 0 15px;
border: 2px dotted lightgray;
padding: 35px 20px;
border-radius: 6px;
display: flex;
flex-direction: column;
align-items: center;
background-color: white;
`;

/**
 * FormField - Invisible file input element overlaid on the drop zone
 *
 * Renders as a transparent file input that captures file selections.
 *
 * @returns {styled.input} Styled file input element
 */
export const FormField = styled.input`
                         font-size: 18px;
display: block;
width: 100%;
border: none;
text-transform: none;
position: absolute;
top: 0;
left: 0;
right: 0;
bottom: 0;
opacity: 0;
&:focus {
     outline: none;
 }
`;

/**
 * InputLabel - Floating label for the file upload input
 *
 * Positioned above the file input field to indicate the upload destination.
 *
 * @returns {styled.label} Styled label element
 */
export const InputLabel = styled.label`
                          top: -21px;
font-size: 13px;
color: black;
left: 0;
position: absolute;
`;

/**
 * DragDropText - Instructions text displayed in the upload area
 *
 * @returns {styled.p} Styled paragraph element
 */
export const DragDropText = styled.p`
                            font-weight: bold;
letter-spacing: 2.2px;
margin-top: 0;
text-align: center;
`;

/**
 * UploadFileBtn - Button to trigger file selection dialog
 *
 * Styled button with hover animation. Displays upload icon and text.
 *
 * @returns {styled.button} Styled button element
 */
export const UploadFileBtn = styled.button`
                             box-sizing: border-box;
appearance: none;
background-color: transparent;
border: 2px solid #3498db;
cursor: pointer;
font-size: 1rem;
line-height: 1;
padding: 1.1em 2.8em;
text-align: center;
text-transform: uppercase;
font-weight: 700;
border-radius: 6px;
color: #3498db;
position: relative;
overflow: hidden;
z-index: 1;
transition: color 250ms ease-in-out;
font-family: "Open Sans", sans-serif;
width: 45%;
display: flex;
align-items: center;
padding-right: 0;
justify-content: center;
&:after {
     content: "";
     position: absolute;
     display: block;
     top: 0;
     left: 50%;
     transform: translateX(-50%);
     width: 0;

     background: #3498db;
     z-index: -1;
     transition: width 250ms ease-in-out;
 }
i {
    font-size: 22px;
    margin-right: 5px;
    border-right: 2px solid;
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    width: 20%;
    display: flex;
    flex-direction: column;
    justify-content: center;
}
@media only screen and (max-width: 500px) {
    width: 70%;
}
@media only screen and (max-width: 350px) {
    width: 100%;
}
&:hover {
     color: #fff;
     outline: 0;
     background: transparent;
&:after {
     width: 110%;
 }
}
&:focus {
     outline: 0;
     background: transparent;
 }
&:disabled {
     opacity: 0.4;
     filter: grayscale(100%);
     pointer-events: none;
 }
`;

/**
 * FilePreviewContainer - Container showing list of files to be uploaded
 *
 * Contains the preview list of selected files with remove options.
 *
 * @returns {styled.article} Styled article element
 */
export const FilePreviewContainer = styled.article`
                                    
span {
    font-size: 14px;
}
`;

/**
 * PreviewList - Grid container for file preview thumbnails
 *
 * Responsive flex-wrap grid that adapts to screen size breakpoints.
 *
 * @returns {styled.section} Styled section element
 */
export const PreviewList = styled.section`
                           display: flex;
flex-wrap: wrap;
margin-top: 10px;
@media only screen and (max-width: 400px) {
    flex-direction: column;
}
`;

/**
 * FileMetaData - Overlay showing file name and size, with remove button
 *
 * Displays filename and size (in KB). Shows remove icon on hover.
 * Hidden for image files when preview is displayed (controlled by isImageFile prop).
 *
 * @returns {styled.div} Styled div element with file metadata
 */
export const FileMetaData = styled.div`
                            display: ${(props) => (props.isImageFile ? "none" : "flex")};
flex-direction: column;
position: absolute;
top: 0;
left: 0;
right: 0;
bottom: 0;
padding: 10px;
border-radius: 6px;
color: white;
font-weight: bold;
background-color: rgba(5, 5, 5, 0.55);
aside {
    margin-top: auto;
    display: flex;
    justify-content: space-between;
}
`;

/**
 * RemoveFileIcon - Trash icon for removing a file from preview
 *
 * @returns {styled.i} Styled icon element (FontAwesome trash)
 */
export const RemoveFileIcon = styled.i`
                              cursor: pointer;
&:hover {
     transform: scale(1.3);
 }
`;

/**
 * PreviewContainer - Individual file preview cell in the grid
 *
 * Aspect-ratio 1:1 container that holds the image preview and metadata overlay.
 * Responsive sizing based on viewport width.
 *
 * @returns {styled.section} Styled section element for one file preview
 */
export const PreviewContainer = styled.section`
                                padding: 0.25rem;
width: 20%;
aspect-ratio: 1/1;
border-radius: 6px;
box-sizing: border-box;
&:hover {
     opacity: 0.55;
${FileMetaData} {
    display: flex;
}
}
& > div:first-of-type {

      position: relative;
  }
@media only screen and (max-width: 750px) {
    width: 25%;
}
@media only screen and (max-width: 500px) {
    width: 50%;
}
@media only screen and (max-width: 400px) {
    width: 100%;
    padding: 0 0 0.4em;
}
`;

/**
 * ImagePreview - Renders the actual image preview for image files
 *
 * @returns {styled.img} Styled img element with cover object-fit
 */
export const ImagePreview = styled.img`
  border-radius: 6px;
  aspect-ratio: 1/1;
  border-radius: 10px;
  object-fit: cover;
  overflow: hidden;
  width: 100%;
`;
